import { useNavigate } from 'react-router-dom'
import { Button } from '@/component/ui/button'
import { PoweredByAida } from '@/component/ui/powered-by-aida'
import { Clock, ListChecks, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { getLatestSurveyAttempt } from '@/services/survey'
import { useSidebarStore } from '../store/sidebare-store'
import { useJsonStore } from '../store/json-store'

export type QuestionnaireIntroProps = {
  userName: string
  studentId: string
}

export const QuestionnaireIntro = ({ userName, studentId }: QuestionnaireIntroProps) => {
  const navigate = useNavigate()
  const { setGenericObject } = useJsonStore()
  const [showConfirm, setShowConfirm] = useState(false)

  useEffect(() => {
    ;(async () => {
      try {
        // Check for latest survey attempt, if there is any
        const surveyAttempt = await getLatestSurveyAttempt(studentId)

        // Update the sidebar accordingly
        if (surveyAttempt.surveyId) {
          useSidebarStore.getState().setSidebarState({ surveyResult: true })
          setGenericObject({ surveyId: surveyAttempt.surveyId })
          localStorage.setItem('currentSurveyId', String(surveyAttempt.surveyId))

          // Already completed — redirect straight to results
          navigate('/student/survey/results', { replace: true })
        }
      } catch {
        // API throws when no completed attempt exists — stay on intro
      }
    })()
  }, [studentId])

  return (
    <section className='w-full min-h-screen bg-white pt-5 md:pt-8 pb-8 px-4 md:px-6'>
      {/* Mobile: 384px max width, Desktop: 1030px max width - Matches Figma */}
      <div className='w-full max-w-[384px] md:max-w-[1030px] mx-auto flex flex-col'>
        {/* Powered by AIDA badge */}
        <PoweredByAida />

        {/* H1: Hi {userName}! */}
        <h1 className='mt-3 md:mt-4 text-[34px] md:text-5xl leading-tight font-bold text-[var(--color-grey-700)] [font-family:var(--font-effra)]'>
          Hi {userName}!
        </h1>

        {/* H2: Welcome heading */}
        <h2 className='mt-2 md:mt-3 text-2xl md:text-[28px] leading-8 md:leading-9 font-bold text-[var(--color-grey-700)] [font-family:var(--font-effra)]'>
          Welcome to Janoos
        </h2>

        {/* Hero Image */}
        <div
          className='mt-4 md:mt-6 w-full overflow-hidden rounded-[16px] bg-no-repeat bg-cover bg-center aspect-[384/268] md:aspect-[1030/360]'
          style={{
            backgroundImage: "url('/janoosbg.png')",
            backgroundColor: 'lightgray',
          }}
          role='img'
          aria-label='Welcome Banner'
        />

        {/* Subheading */}
        <p className='mt-4 md:mt-6 text-lg md:text-xl leading-7 font-bold text-[var(--color-grey-700)] [font-family:var(--font-effra)]'>
          Complete this 2 min questionnaire to unlock your learning modality.
        </p>

        {/* Body Paragraph */}
        <p className='mt-3 md:mt-4 text-lg leading-relaxed font-normal text-[var(--color-grey-600)] [font-family:var(--font-proxima-nova)]'>
          Grounded in trusted educational research the Janoos toolset will be available to you 24/7 to help with your studies, assignments, feedback and quick
          answers. This first tool will provide you with a personalised view of your learning modality preferences. Stay tuned for the next set of tools.
        </p>

        {/* Meta Info - Time & Questions */}
        <div className='mt-3 md:mt-4 flex flex-col gap-2'>
          <div className='flex items-center gap-2 text-base text-[var(--color-grey-600)] [font-family:var(--font-proxima-nova)]'>
            <Clock className='h-4 w-4 flex-shrink-0' />
            <span className='font-bold'>Time required: 2 min</span>
          </div>
          <div className='flex items-center gap-2 text-base text-[var(--color-grey-600)] [font-family:var(--font-proxima-nova)]'>
            <ListChecks className='h-4 w-4 flex-shrink-0' />
            <span className='font-bold'>Questions: 20</span>
          </div>
        </div>

        {/* CTA Button */}
        <Button variant='primary' onClick={() => setShowConfirm(true)} size='cta' className='mt-6 self-start'>
          Let&apos;s Begin
        </Button>
      </div>

      {/* direct-invoke submission warning dialog */}
      {showConfirm && (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm'>
          <div className='inline-flex flex-col items-end gap-5 bg-white rounded-2xl shadow-2xl p-6 pb-10 w-[509px] max-w-[calc(100vw-2rem)]'>
            {/* Close button */}
            <button onClick={() => setShowConfirm(false)} className='text-[var(--color-curtin-primary-black-90)] hover:opacity-70' aria-label='Close'>
              <X className='h-5 w-5' />
            </button>

            {/* Text + Button container */}
            <div className='flex flex-col items-center gap-8 w-full'>
              <p className='text-center text-lg leading-relaxed text-[var(--color-curtin-primary-black-90)] [font-family:var(--font-proxima-nova)]'>
                <strong>Please note:</strong> You can only complete this questionnaire once. Take your time and answer carefully. Once you submit, you&apos;ll
                be able to view your results.
              </p>
              <button
                onClick={() => navigate('/student/survey/question/1')}
                className='text-white text-lg leading-relaxed px-8 py-3 rounded-[5px] bg-[var(--primary)] hover:opacity-90 [font-family:var(--font-proxima-nova)]'>
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  )
}
